/**
 * Ask FusionDocs a question in plain words: a slide-over (full screen on a phone) with the answer rendered by the
 * safe Markdown renderer and numbered sources; a source that names a FusionDocs document opens it in the preview.
 */
import { useMutation } from "@tanstack/react-query";
import { FileText, Loader2, MessageSquareText, Send, X } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { post } from "../../lib/api";
import { Markdown } from "../Markdown";
import { ErrorNote } from "../ui";
import { Snip } from "./DocMeta";
import { idFromUrl, type FdAnswer } from "./fd";

export function AskPanel({ onClose, onOpen }: { onClose: () => void; onOpen: (id: string) => void }) {
  const [question, setQuestion] = useState("");
  const box = useRef<HTMLTextAreaElement>(null);
  const ask = useMutation({ mutationFn: (q: string) => post<FdAnswer>("/api/fd/ask", { question: q }) });
  useEffect(() => box.current?.focus(), []);
  useEffect(() => {
    const esc = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", esc);
    return () => window.removeEventListener("keydown", esc);
  }, [onClose]);

  const submit = () => {
    const q = question.trim();
    if (q.length >= 3 && !ask.isPending) ask.mutate(q);
  };
  const a = ask.data;

  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-[#0b0c2a]/30 backdrop-blur-[1px]" onClick={onClose}>
      <aside
        role="dialog"
        aria-modal="true"
        aria-label="Ask your documents"
        onClick={(e) => e.stopPropagation()}
        className="flex h-full w-full flex-col bg-[var(--panel)] pb-[env(safe-area-inset-bottom)] shadow-[var(--shadow-pop)] sm:max-w-[460px] sm:border-l sm:border-[var(--line)]"
      >
        <header className="flex items-center gap-2 border-b border-[var(--line)] px-4 py-3">
          <MessageSquareText size={17} className="text-brand-600" aria-hidden />
          <h2 className="flex-1 text-[0.9375rem] font-bold">Ask your documents</h2>
          <button className="btn btn-ghost btn-icon !h-9 !min-h-0 !w-9" onClick={onClose} aria-label="Close"><X size={17} /></button>
        </header>

        <form className="border-b border-[var(--line)] p-4" onSubmit={(e) => { e.preventDefault(); submit(); }}>
          <label htmlFor="fd-ask" className="sr-only">Your question</label>
          <textarea
            id="fd-ask"
            ref={box}
            className="input min-h-[4.5rem] w-full resize-y"
            maxLength={2000}
            placeholder="e.g. When does the office lease renew, and what is the notice period?"
            value={question}
            onChange={(e) => setQuestion(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) { e.preventDefault(); submit(); } }}
          />
          <div className="mt-2 flex items-center justify-between gap-2">
            <span className="muted text-xs">Answers come only from documents you can see.</span>
            <button className="btn btn-primary btn-sm" type="submit" disabled={question.trim().length < 3 || ask.isPending}>
              {ask.isPending ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />} Ask
            </button>
          </div>
        </form>

        <div className="scroll-thin min-h-0 flex-1 space-y-4 overflow-auto p-4" aria-live="polite">
          {ask.isPending && <p className="muted flex items-center gap-2 text-sm"><Loader2 size={14} className="animate-spin" /> Reading your documents…</p>}
          {ask.error && <ErrorNote error={ask.error} />}
          {a && !ask.isPending && (
            <>
              <div className="text-sm leading-relaxed">
                <Markdown text={a.answer || (a.found ? "" : "Nothing in your documents answers that.")} />
              </div>
              {a.sources.length > 0 && (
                <section>
                  <h3 className="section-label mb-2">Sources</h3>
                  <ol className="space-y-2">
                    {a.sources.map((s) => {
                      const id = idFromUrl(s.url);
                      const body = (
                        <>
                          <span className="flex items-center gap-2 text-sm font-semibold">
                            <span className="grid h-5 w-5 shrink-0 place-items-center rounded bg-brand-50 text-[0.7rem] font-bold text-brand-700 num">{s.n}</span>
                            <FileText size={14} className="muted shrink-0" aria-hidden />
                            <span className="min-w-0 flex-1 truncate">{s.title || "Untitled"}</span>
                          </span>
                          {s.snippet && <span className="muted mt-1 line-clamp-3 block text-xs"><Snip text={s.snippet} /></span>}
                        </>
                      );
                      const cls = "block w-full rounded-xl border border-[var(--line)] bg-[var(--panel)] p-3 text-left";
                      return (
                        <li key={s.n}>
                          {id
                            ? <button type="button" className={`${cls} hover:border-brand-300`} onClick={() => { onOpen(id); onClose(); }} aria-label={`Open source ${s.n}: ${s.title}`}>{body}</button>
                            : <div className={cls}>{body}</div>}
                        </li>
                      );
                    })}
                  </ol>
                </section>
              )}
            </>
          )}
          {!a && !ask.isPending && !ask.error && (
            <p className="muted text-sm">Ask about contracts, renewals, policies or anything filed in FusionDocs.</p>
          )}
        </div>
      </aside>
    </div>
  );
}
